<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->
<template>
  <!-- Grid mode -->
  <div v-if="natures.length <= gridThreshold">
    <div v-if="categoryFilterOptions.length > 0" class="nature-category-filter">
      <PrimeDropdown
        v-model="selectedCategory"
        :options="categoryFilterOptions"
        optionLabel="label"
        optionValue="value"
        :placeholder="t('classificationPlan.filterByCategory')"
        :disabled="disableCategoryFilter"
        class="w-full"
      >
        <template #dropdownicon>
          <i class="ri-filter-3-line"></i>
        </template>
      </PrimeDropdown>
    </div>
    <div class="nature-grid">
      <div v-if="allowNoNature" class="nature-grid-item">
        <div
          class="nature-card nature-card-no-nature"
          :class="[
            modelValue === NO_NATURE_CODE
              ? 'nature-card--selected'
              : 'nature-card--default',
          ]"
          @click="$emit('select-no-nature')"
        >
          <i class="ri-file-line" style="font-size: 2rem"></i>
          <div class="nature-card-label nature-card-label--muted">
            {{ t("classificationPlan.noNature") }}
          </div>
        </div>
      </div>
      <div
        v-for="nature in categoryFilteredNatures"
        :key="nature.code"
        class="nature-grid-item"
      >
        <div
          class="nature-card"
          :class="[
            modelValue === nature.code
              ? 'nature-card--selected'
              : 'nature-card--default',
          ]"
          @click="$emit('update:modelValue', nature.code)"
        >
          <i :class="nature.icon || 'ri-file-line'" style="font-size: 2rem"></i>
          <div class="nature-card-label">{{ nature.name }}</div>
        </div>
      </div>
    </div>
  </div>

  <!-- Table mode -->
  <template v-else>
    <div class="nature-toolbar">
      <PrimeIconField class="nature-toolbar-search">
        <PrimeInputIcon class="ri-search-line" />
        <PrimeInputText
          v-model="natureSearch"
          :placeholder="t('classificationPlan.searchNaturePlaceholder')"
          class="full-width"
        />
        <PrimeInputIcon
          v-if="natureSearch"
          class="ri-close-line nature-search-clear"
          @click="natureSearch = ''"
        />
      </PrimeIconField>
      <PrimeDropdown
        v-if="categoryFilterOptions.length > 0"
        v-model="selectedCategory"
        :options="categoryFilterOptions"
        optionLabel="label"
        optionValue="value"
        :placeholder="t('classificationPlan.filterByCategory')"
        :disabled="disableCategoryFilter"
        class="nature-toolbar-category"
      >
        <template #dropdownicon>
          <i class="ri-filter-3-line"></i>
        </template>
      </PrimeDropdown>
    </div>
    <PrimeDataTable
      v-model:selection="selectedObject"
      :value="tableOptions"
      selection-mode="single"
      dataKey="code"
      :paginator="tableOptions.length > 12"
      :rows="12"
      :rowsPerPageOptions="[12, 24, 50]"
      size="small"
      rowHover
      :rowClass="rowClass"
      @row-select="onRowSelect"
    >
      <PrimeColumn field="icon" header="" style="width: 3rem">
        <template #body="slotProps">
          <i
            :class="slotProps.data.icon || 'ri-file-line'"
            style="font-size: 1.5rem"
          ></i>
        </template>
      </PrimeColumn>
      <PrimeColumn field="name" :header="t('classificationPlan.natureColumn')">
        <template #body="slotProps">
          <span
            :class="{
              'nature-name-muted': slotProps.data.code === NO_NATURE_CODE,
            }"
            >{{ slotProps.data.name }}</span
          >
        </template>
      </PrimeColumn>
      <PrimeColumn
        field="code"
        :header="t('classificationPlan.code')"
        style="width: 15rem"
      >
        <template #body="slotProps">
          <code v-if="slotProps.data.code !== NO_NATURE_CODE">{{
            slotProps.data.code
          }}</code>
          <span v-else class="nature-name-muted">--</span>
        </template>
      </PrimeColumn>
      <PrimeColumn
        v-if="categories.length > 0"
        :header="t('classificationPlan.category')"
      >
        <template #body="slotProps">
          <PrimeTag
            v-for="catId in slotProps.data.categories || []"
            :key="catId"
            :value="getCategoryLabel(catId)"
            class="mr-1"
          />
        </template>
      </PrimeColumn>
    </PrimeDataTable>
  </template>
</template>

<script>
import { computed, ref, watch } from "vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import IconField from "primevue/iconfield";
import InputIcon from "primevue/inputicon";
import Dropdown from "primevue/dropdown";
import Tag from "primevue/tag";
import { useI18n } from "vue-i18n";

const ALL_CATEGORIES_CODE = "__ALL_CATEGORIES__";
const NO_CATEGORY_CODE = "__NO_CATEGORY__";

const NO_NATURE_CODE = "__NO_NATURE__";

export default {
  name: "NatureSelector",
  components: {
    PrimeDataTable: DataTable,
    PrimeColumn: Column,
    PrimeInputText: InputText,
    PrimeIconField: IconField,
    PrimeInputIcon: InputIcon,
    PrimeDropdown: Dropdown,
    PrimeTag: Tag,
  },
  props: {
    natures: { type: Array, required: true },
    modelValue: { type: String, default: null },
    allowNoNature: { type: Boolean, default: false },
    gridThreshold: { type: Number, default: 4 },
    categories: { type: Array, default: () => [] },
    defaultCategory: { type: String, default: null },
    disableCategoryFilter: { type: Boolean, default: false },
  },
  emits: ["update:modelValue", "select-no-nature"],
  setup(props, { emit }) {
    const { t } = useI18n();
    const natureSearch = ref("");
    const selectedObject = ref(null);
    const selectedCategory = ref(props.defaultCategory || ALL_CATEGORIES_CODE);

    watch(
      () => props.defaultCategory,
      (val) => {
        if (val) {
          selectedCategory.value = val;
        }
      },
    );

    const categoryFilterOptions = computed(() => {
      if (!props.categories || props.categories.length === 0) {
        return [];
      }
      const options = [
        {
          label: t("classificationPlan.allCategories"),
          value: ALL_CATEGORIES_CODE,
        },
        ...props.categories.map((c) => ({ label: c.label, value: c.id })),
        { label: t("classificationPlan.noCategory"), value: NO_CATEGORY_CODE },
      ];
      return options;
    });

    const categoryFilteredNatures = computed(() => {
      if (selectedCategory.value === ALL_CATEGORIES_CODE) {
        return props.natures;
      }
      if (selectedCategory.value === NO_CATEGORY_CODE) {
        return props.natures.filter(
          (n) => !n.categories || n.categories.length === 0,
        );
      }
      return props.natures.filter(
        (n) => n.categories && n.categories.includes(selectedCategory.value),
      );
    });

    const filteredNatures = computed(() => {
      const search = natureSearch.value.toLowerCase().trim();
      if (!search) {
        return categoryFilteredNatures.value;
      }
      return categoryFilteredNatures.value.filter(
        (n) =>
          n.name.toLowerCase().includes(search) ||
          n.code.toLowerCase().includes(search),
      );
    });

    const noNatureEntry = computed(() => ({
      code: NO_NATURE_CODE,
      name: t("classificationPlan.noNature"),
      icon: "ri-file-line",
    }));

    const tableOptions = computed(() => {
      const options = [...filteredNatures.value];
      const isSearching = natureSearch.value.trim().length > 0;
      if (props.allowNoNature && !isSearching) {
        options.unshift(noNatureEntry.value);
      }
      return options;
    });

    const rowClass = (data) => {
      if (data.code === NO_NATURE_CODE) {
        return "nature-row-no-nature";
      }
      return "";
    };

    const onRowSelect = (event) => {
      if (event.data.code === NO_NATURE_CODE) {
        emit("select-no-nature");
      } else {
        emit("update:modelValue", event.data.code);
      }
    };

    const getCategoryLabel = (catId) => {
      const cat = props.categories.find((c) => c.id === catId);
      return cat ? cat.label : catId;
    };

    return {
      t,
      natureSearch,
      selectedObject,
      selectedCategory,
      categoryFilterOptions,
      categoryFilteredNatures,
      tableOptions,
      rowClass,
      onRowSelect,
      getCategoryLabel,
      NO_NATURE_CODE,
    };
  },
};
</script>

<style scoped>
.full-width {
  width: 100%;
}

.nature-category-filter {
  margin-bottom: 0.5rem;
}

.nature-toolbar {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.nature-toolbar-search {
  flex: 1;
  min-width: 0;
}

.nature-toolbar-category {
  min-width: 14rem;
}

.nature-search-clear {
  cursor: pointer;
}

.nature-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.nature-grid-item {
  flex: 1 1 0;
  min-width: 0;
  padding: 0.5rem;
}

.nature-card {
  padding: 0.75rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius);
  cursor: pointer;
  text-align: center;
  transition:
    background-color 0.2s,
    color 0.2s;
}

.nature-card--selected {
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color);
}

.nature-card--default {
  background-color: var(--p-surface-card);
}

.nature-card--default:hover {
  background-color: var(--p-content-hover-background);
}

.nature-card-label {
  margin-top: 0.5rem;
}

.nature-card-label--muted {
  font-style: italic;
  color: var(--p-text-muted-color);
}

.nature-card-no-nature {
  border-style: dashed;
}

:deep(.p-datatable-thead th),
:deep(.p-datatable-tbody td) {
  text-align: left;
}

:deep(.p-datatable-tbody tr) {
  cursor: pointer;
}

:deep(.nature-row-no-nature) {
  font-style: italic;
  color: var(--p-text-muted-color);
  border-bottom: 1px solid var(--p-content-border-color);
}

.nature-name-muted {
  font-style: italic;
  color: var(--p-text-muted-color);
}
</style>
